import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Paper,
  Grid2,
  Snackbar,
} from "@mui/material";
import SearchSelect from "../../Components/SearchSelect";
import MultiSelect from "../../Components/MultiSelect";
import dayjs from "dayjs";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import { decryptTheParams, encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import DigitalTwinTableSearch from "../../Components/DigitalTwinTableSearch";
import { getDatePickerDate, convertZonetoUTC } from "../../Helper/DatePicker/DateConverter";
import {
  getDownloadTypes,
  getDownloadTypeColumns,
  getOrgCanIds,
  submitDownloadRequest,
} from "../../Api/DataDownloadApi";
import { getDigitalTwinList } from "../../Api/DigitalTwinListApi";
import AntDateAndTimePicker from "../../Helper/DatePicker/AntDateAndTimePicker";
import GenericDraggableTableView from "../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import DeviceIDLink from "../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import {
  VEHICLE_TABLE_COLUMNS,
  VEHICLE_COLUMN_ORDER,
} from "./Data/VehicleSearchTableColumns";


const cookies = new Cookies();

const TIME_PRESETS = [
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "12h", hours: 12 },
  { label: "24h", hours: 24 },
  { label: "Custom", hours: null },
];

const VEHICLE_SEARCH_FIELDS = [
  { key: "licensePlateNo", title: "Bus Number" },
  { key: "deviceID", title: "IMEI" },
  { key: "vin", title: "Chassis Number" },
];


const SectionCard = ({ children }) => (
  <Paper variant="outlined" sx={{ borderRadius: "8px", p: 3, borderColor: "#E4E4E4" }}>
    {children}
  </Paper>
);

const SectionTitle = ({ title, subtitle }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#2E2E2E" }}>{title}</Typography>
    {subtitle && (
      <Typography sx={{ fontSize: "13px", color: "#6B6B6B", mt: 0.25 }}>{subtitle}</Typography>
    )}
  </Box>
);


const NewDownloadMain = ({ onBack }) => {
  const navigate = useNavigate();
  const queryParams = decryptTheParams();

  const vehicleColumns = useMemo(() => getColumnsConfig({
    tableColumns: VEHICLE_TABLE_COLUMNS,
    tableColumnOrder: VEHICLE_COLUMN_ORDER,
    customRenderers: {
      "device-id-link": (record, config) => (
        <DeviceIDLink
          record={record}
          config={config}
          onTextClick={() => {
            const temp = {
              ...queryParams,
              batteryID: record.batteryID,
              deviceID: record.deviceID,
              batterySearchedGlobal: "false",
              prevLink: "/",
              navigateFromTab: true,
              tabValue: "overview",
            };
            encryptTheParams(temp, navigate, true);
          }}
        />
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [navigate]);

  // ── Date & Time Range ────────────────────────────────────────────
  const [timeMode, setTimeMode] = useState("recent");
  const [selectedPreset, setSelectedPreset] = useState("6h");
  const [customTimeRange, setCustomTimeRange] = useState(null); // { startDate, endDate } in "YYYY-MM-DD HH:mm:ss"
  const today = getDatePickerDate(undefined, 0);
  const sixMonthsAgo = dayjs().subtract(6, "month");
  const twentyFourHoursAgo = dayjs().subtract(24, "hour");
  const [historicalTimeRange, setHistoricalTimeRange] = useState(null); // { startDate, endDate } in "YYYY-MM-DD HH:mm:ss"

  // Derived from/to for display and payload
  const getFromTo = () => {
    if (timeMode === "recent") {
      if (selectedPreset === "Custom") {
        if (!customTimeRange) {
          return { fromTime: "", toTime: "", fromDisplay: "—", toDisplay: "—" };
        }
        return {
          fromTime: customTimeRange.startDate,
          toTime: customTimeRange.endDate,
          fromDisplay: dayjs(customTimeRange.startDate).format("DD MMM YYYY, hh:mm a"),
          toDisplay: dayjs(customTimeRange.endDate).format("DD MMM YYYY, hh:mm a"),
        };
      }
      const hoursMap = { "1h": 1, "6h": 6, "12h": 12, "24h": 24 };
      const hours = hoursMap[selectedPreset] || 6;
      return {
        fromTime: dayjs().subtract(hours, "hour").format("YYYY-MM-DD HH:mm:ss"),
        toTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        fromDisplay: dayjs().subtract(hours, "hour").format("DD MMM YYYY, hh:mm a"),
        toDisplay: dayjs().format("DD MMM YYYY, hh:mm a"),
      };
    }
    if (!historicalTimeRange) {
      return { fromTime: "", toTime: "", fromDisplay: "—", toDisplay: "—" };
    }
    return {
      fromTime: historicalTimeRange.startDate,
      toTime: historicalTimeRange.endDate,
      fromDisplay: dayjs(historicalTimeRange.startDate).format("DD MMM YYYY, hh:mm a"),
      toDisplay: dayjs(historicalTimeRange.endDate).format("DD MMM YYYY, hh:mm a"),
    };
  };

  // ── Snackbar notification ────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const showSnackbar = (message, severity = "info") => setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  // ── Vehicle search & table ───────────────────────────────────────
  const [vehicleTableData, setVehicleTableData] = useState([]);
  const [vehicleTableLoading, setVehicleTableLoading] = useState(false);
  const [vehicleHasSearched, setVehicleHasSearched] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(""); // single batteryID

  const handleVehicleSearch = async (devices, columnKey) => {
    if (!devices || devices.length === 0) {
      setVehicleTableData([]);
      setSelectedVehicleId("");
      setVehicleHasSearched(false);
      return;
    }
    setVehicleTableLoading(true);
    setVehicleHasSearched(true);
    try {
      const res = await getDigitalTwinList({
        pageNumber: 1,
        rowsPerPage: 50,
        startDate: today,
        endDate: today,
        filters: { [columnKey]: devices },
      });
      const records = res?.response?.data || [];
      setVehicleTableData(records);
      setSelectedVehicleId(records.length === 1 ? (records[0].batteryID || "") : "");
    } catch (e) {
      console.error("Failed to fetch vehicle records", e);
      setVehicleTableData([]);
      setSelectedVehicleId("");
    } finally {
      setVehicleTableLoading(false);
    }
  };

  // Array of selected vehicles for payload (single item)
  const selectedVehicles = selectedVehicleId ? [selectedVehicleId] : [];

  // ── Data Category ────────────────────────────────────────────────
  const [downloadTypes, setDownloadTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [availableCanIds, setAvailableCanIds] = useState([]);
  const [selectedCanIds, setSelectedCanIds] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);

  // ── Confirm dialog ───────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const notifyEmail = cookies.get("email") || cookies.get("name") || "";

  // Load download types
  useEffect(() => {
    const load = async () => {
      setLoadingTypes(true);
      try {
        const res = await getDownloadTypes();
        const types = res?.response?.data || [];
        setDownloadTypes(types);
        if (types.length > 0) {
          setSelectedTypeId(types[0].id);
          setSelectedType(types[0]);
        }
      } catch (e) {
        console.error("Failed to load download types", e);
      } finally {
        setLoadingTypes(false);
      }
    };
    load();
  }, []);

  // Load CAN IDs
  useEffect(() => {
    const load = async () => {
      try {
        const res = await getOrgCanIds();
        setAvailableCanIds(res?.response?.data || []);
      } catch (e) {
        console.error("Failed to load CAN IDs", e);
      }
    };
    load();
  }, []);

  // Load columns when type changes
  useEffect(() => {
    if (!selectedTypeId) return;
    const type = downloadTypes.find((t) => t.id === selectedTypeId);
    setSelectedType(type || null);
    setSelectedColumns([]);
    setSelectedCanIds([]);

    if (!type?.supportsAdditionalColumns) {
      setAvailableColumns([]);
      return;
    }
    const load = async () => {
      setLoadingColumns(true);
      try {
        const res = await getDownloadTypeColumns(selectedTypeId);
        setAvailableColumns(res?.response?.data || []);
      } catch (e) {
        setAvailableColumns([]);
      } finally {
        setLoadingColumns(false);
      }
    };
    load();
  }, [selectedTypeId, downloadTypes]);


  const customRangeValid = selectedPreset !== "Custom" || !!customTimeRange;
  const canIdRequired = !!(selectedType?.supportsCanIdFilter && availableCanIds.length > 0);
  const isFormValid = !!selectedVehicleId && selectedTypeId !== "" && (timeMode === "recent" ? customRangeValid : !!historicalTimeRange) && (!canIdRequired || selectedCanIds.length > 0);

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    const { fromTime, toTime } = getFromTo();
    try {
      const payload = {
        dataDownloadTypeID: selectedTypeId,
        timeMode: timeMode === "recent" ? "immediate" : "historical",
        fromTime: convertZonetoUTC(fromTime),
        toTime: convertZonetoUTC(toTime),
        batteryIDs: selectedVehicles,
        canIDs: selectedCanIds,
        additionalColumns: selectedColumns,
      };
      await submitDownloadRequest(payload);
      setConfirmOpen(false);
      onBack();
    } catch (e) {
      setSubmitError("Failed to submit download request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const { fromDisplay, toDisplay } = getFromTo();

  return (
    <Grid2 container flexDirection="column" gap={2}>
      {/* Breadcrumb Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          onClick={onBack}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            gap: "8px",
            "&:hover": { opacity: 0.7 },
          }}
        >
          <ChevronLeftIcon style={{ width: 20, height: 20 }} />
          <Typography
            variant="fw500s16c000000"
            sx={{ color: "#9E9E9E", lineHeight: "28px" }}
          >
            Downloads/
          </Typography>
        </Box>
        <Typography
          variant="fw500s16c000000"
          sx={{ color: "#2E2E2E", lineHeight: "28px" }}
        >
          New Download
        </Typography>
      </Box>

      {/* ── Section 1: Date & Time Range ── */}
      <SectionCard>
        <SectionTitle
          title="Date & Time Range"
          subtitle="Choose the type of data you want to download"
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {/* Historical / Recent toggle */}
          <Button
            variant="outlined"
            onClick={() => setTimeMode("historical")}
            startIcon={<span style={{ fontSize: "14px" }}>📅</span>}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              bgcolor: timeMode === "historical" ? "#4D44D8" : "transparent",
              borderColor: timeMode === "historical" ? "#4D44D8" : "#BBBBBB",
              color: timeMode === "historical" ? "#fff" : "#555",
              fontSize: "13px",
              px: 2,
              "&:hover": { bgcolor: timeMode === "historical" ? "#3a32c0" : "transparent", borderColor: timeMode === "historical" ? "#3a32c0" : "#BBBBBB" },
            }}
          >
            Historical
          </Button>
          <Button
            variant="outlined"
            onClick={() => setTimeMode("recent")}
            startIcon={<span style={{ fontSize: "14px" }}>🕐</span>}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              bgcolor: timeMode === "recent" ? "#4D44D8" : "transparent",
              borderColor: timeMode === "recent" ? "#4D44D8" : "#BBBBBB",
              color: timeMode === "recent" ? "#fff" : "#555",
              fontSize: "13px",
              px: 2,
              "&:hover": { bgcolor: timeMode === "recent" ? "#3a32c0" : "transparent", borderColor: timeMode === "recent" ? "#3a32c0" : "#BBBBBB" },
            }}
          >
            Recent
          </Button>

          {/* Recent presets */}
          {timeMode === "recent" && TIME_PRESETS.map((preset) => (
            <Button
              key={preset.label}
              variant="outlined"
              onClick={() => setSelectedPreset(preset.label)}
              sx={{
                minWidth: "44px",
                textTransform: "none",
                borderRadius: "8px",
                px: 1.5,
                fontSize: "13px",
                bgcolor: selectedPreset === preset.label ? "#4D44D8" : "transparent",
                borderColor: selectedPreset === preset.label ? "#4D44D8" : "#BBBBBB",
                color: selectedPreset === preset.label ? "#fff" : "#555",
                "&:hover": { bgcolor: selectedPreset === preset.label ? "#3a32c0" : "transparent", borderColor: selectedPreset === preset.label ? "#3a32c0" : "#BBBBBB" },
              }}
            >
              {preset.label}
            </Button>
          ))}

          {/* Historical: date+time picker */}
          {timeMode === "historical" && (
            <>
              <AntDateAndTimePicker
                handleOnChange={(range) => setHistoricalTimeRange(range)}
                minDateTime={sixMonthsAgo}
                maxDateTime={dayjs().startOf("day").subtract(1, "second")}
                maxRangeHours={30 * 24}
                onMaxRangeExceeded={({ adjustedEndDate }) =>
                  showSnackbar(
                    `Selection exceeds 30 days. End date+time auto-adjusted to ${dayjs(adjustedEndDate).format("DD MMM YYYY, hh:mm a")}.`,
                    "warning"
                  )
                }
              />
              {historicalTimeRange && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor: "#F0FFF4",
                    borderLeft: "3px solid #2E7D32",
                    borderRadius: "0 6px 6px 0",
                    px: 1.5,
                    py: 0.75,
                    cursor: "default",
                    userSelect: "none",
                  }}
                >
                  <Typography sx={{ fontSize: "11px", color: "#6B6B6B", fontWeight: 500, lineHeight: 1 }}>
                    Selected
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#1B5E20", fontWeight: 600, lineHeight: 1 }}>
                    {dayjs(historicalTimeRange.startDate).format("DD MMM YYYY, hh:mm a")}
                    &nbsp;–&nbsp;
                    {dayjs(historicalTimeRange.endDate).format("DD MMM YYYY, hh:mm a")}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Recent: custom picker inline */}
          {timeMode === "recent" && selectedPreset === "Custom" && (
            <>
              <AntDateAndTimePicker
                handleOnChange={(range) => setCustomTimeRange(range)}
                minDateTime={twentyFourHoursAgo}
                maxRangeHours={24}
              />
              {customTimeRange && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor: "#F0FFF4",
                    borderLeft: "3px solid #2E7D32",
                    borderRadius: "0 6px 6px 0",
                    px: 1.5,
                    py: 0.75,
                    cursor: "default",
                    userSelect: "none",
                  }}
                >
                  <Typography sx={{ fontSize: "11px", color: "#6B6B6B", fontWeight: 500, lineHeight: 1 }}>
                    Selected
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#1B5E20", fontWeight: 600, lineHeight: 1 }}>
                    {dayjs(customTimeRange.startDate).format("DD MMM YYYY, hh:mm a")}
                    &nbsp;–&nbsp;
                    {dayjs(customTimeRange.endDate).format("DD MMM YYYY, hh:mm a")}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Recent: quick-preset range display */}
        {timeMode === "recent" && selectedPreset !== "Custom" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1.5, flexWrap: "wrap" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                bgcolor: "#F0FFF4",
                borderLeft: "3px solid #2E7D32",
                borderRadius: "0 6px 6px 0",
                px: 1.5,
                py: 0.75,
                userSelect: "none",
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#6B6B6B", fontWeight: 500, lineHeight: 1 }}>
                Selected
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#1B5E20", fontWeight: 600, lineHeight: 1 }}>
                {fromDisplay}&nbsp;–&nbsp;{toDisplay}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "12px", color: "#4D44D8" }}>
              Recent data may be incomplete for the last few minutes.
            </Typography>
          </Box>
        )}
      </SectionCard>

      {/* ── Section 2: Vehicle ── */}
      <SectionCard>
        <SectionTitle
          title="Vehicle"
          subtitle="Search and select vehicles for the export"
        />
        <DigitalTwinTableSearch
          onSearch={handleVehicleSearch}
          searchableFields={VEHICLE_SEARCH_FIELDS}
          defaultColumn="licensePlateNo"
          containerWidth="100%"
          maxSelections={1}
        />

        {vehicleTableLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <CircularProgress size={16} sx={{ color: "#4D44D8" }} />
            <Typography sx={{ fontSize: "13px", color: "#6B6B6B" }}>Searching…</Typography>
          </Box>
        )}

        {!vehicleTableLoading && vehicleHasSearched && vehicleTableData.length === 0 && (
          <Typography sx={{ fontSize: "13px", color: "#9B9B9B", mt: 1.5 }}>
            No vehicles found. Try a different search term.
          </Typography>
        )}

        {vehicleTableData.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "13px", color: selectedVehicleId ? "#2E7D32" : "#6B6B6B", mb: 1, fontWeight: selectedVehicleId ? 500 : 400 }}>
              {selectedVehicleId
                ? `✓ 1 vehicle selected${vehicleTableData.find((r) => r.batteryID === selectedVehicleId)?.licensePlateNo ? ` (${vehicleTableData.find((r) => r.batteryID === selectedVehicleId).licensePlateNo})` : ""}`
                : `${vehicleTableData.length} result${vehicleTableData.length > 1 ? "s" : ""} found — select a vehicle`}
            </Typography>
            <GenericDraggableTableView
              tableID="VehicleSearchTable"
              disableTableHeader={true}
              disableColumnSettings={true}
              disableSearch={true}
              disableDownloads={true}
              disableFiltersSettings={true}
              disablePagination={true}
              filters={{}}
              selectedFilters={{}}
              applyFilters={() => {}}
              applySearch={() => {}}
              initialColumns={vehicleColumns}
              tableColumns={vehicleColumns}
              selectedColumns={VEHICLE_COLUMN_ORDER}
              applySelectedColumns={() => {}}
              tableData={vehicleTableData.map((r) => ({ ...r, key: r.batteryID || r.deviceID }))}
              onColumnOrderChange={() => {}}
              columnOrder={VEHICLE_COLUMN_ORDER.map((k) => VEHICLE_TABLE_COLUMNS[k]?.title)}
              isLoading={vehicleTableLoading}
              pageNumber={1}
              rowsPerPage={vehicleTableData.length || 50}
              count={vehicleTableData.length}
              total={vehicleTableData.length}
              applySort={() => {}}
              onPageChange={() => {}}
              emptyMessage="No vehicles found"
              rowSelection={{
                type: "radio",
                selectedRowKeys: selectedVehicleId
                  ? vehicleTableData
                      .filter((r) => r.batteryID === selectedVehicleId)
                      .map((r) => r.id)
                  : [],
                onChange: (keys) => {
                  const id = keys[0];
                  if (!id) { setSelectedVehicleId(""); return; }
                  const record = vehicleTableData.find((r) => String(r.id) === String(id));
                  setSelectedVehicleId(record?.batteryID || "");
                },
                columnWidth: 48,
              }}
              containerStyles={{ border: "none", px: 0, py: 0, borderRadius: 0 }}
            />
          </Box>
        )}
      </SectionCard>

      {/* ── Section 3: Data Category ── */}
      <SectionCard>
        <SectionTitle
          title="Data Category"
          subtitle="Select the type of data to export"
        />

        {/* Download type selector */}
        <Box sx={{ mb: 2 }}>
          <SearchSelect
            options={downloadTypes.map((t) => ({ id: t.id, label: t.label }))}
            value={selectedTypeId || null}
            onChange={(id) => setSelectedTypeId(id)}
            placeholder="Search data category…"
            loading={loadingTypes}
          />
        </Box>

        {/* CAN IDs autocomplete */}
        {selectedType?.supportsCanIdFilter && availableCanIds.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#2E2E2E" }}>
                CAN IDs
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#D32F2F" }}>
                *required
              </Typography>
            </Box>
            <MultiSelect
              options={availableCanIds.map((c) => ({ id: c.canID, label: c.canID }))}
              value={selectedCanIds}
              onChange={(ids) => setSelectedCanIds(ids)}
              placeholder="Search, select, or paste comma-separated CAN IDs…"
              enableBulkPaste
              showSelectAll
            />
          </Box>
        )}

        {/* Vehicle Information Columns autocomplete */}
        {selectedType?.supportsAdditionalColumns && (
          <Box>
            <Box sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#2E2E2E" }}>
                Vehicle Information Columns
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#9B9B9B", mt: 0.25 }}>
                Additional vehicle metadata columns to append to each row (optional)
              </Typography>
            </Box>
            {loadingColumns ? (
              <CircularProgress size={16} sx={{ color: "#4D44D8" }} />
            ) : (
              <MultiSelect
                options={availableColumns.map((col) => ({ id: col.columnKey, label: col.label }))}
                value={selectedColumns}
                onChange={(ids) => setSelectedColumns(ids)}
                placeholder="Search or select columns…"
                showSelectAll
              />
            )}
          </Box>
        )}
      </SectionCard>

      {/* ── Download Summary ── */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: "8px", p: 2.5, borderColor: "#E4E4E4", bgcolor: "#FAFAFA" }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "15px", mb: 2, color: "#1A1A1A" }}>
          Download Summary
        </Typography>
        {[
          { label: "Mode", value: timeMode === "recent" ? "Immediate / Recent" : "Historical" },
          { label: "From", value: fromDisplay },
          { label: "To", value: toDisplay },
          {
            label: "Vehicles",
            value: selectedVehicleId
              ? vehicleTableData.find((r) => r.batteryID === selectedVehicleId)?.licensePlateNo
                || selectedVehicleId
              : "—",
          },
          { label: "Data Category", value: selectedType?.label || "—" },
          {
            label: "CAN IDs",
            value: selectedCanIds.length === 0 ? (canIdRequired ? "— (required)" : "All") : `${selectedCanIds.length} selected`,
          },
          {
            label: "Vehicle Columns",
            value: selectedColumns.length === 0 ? "None" : `${selectedColumns.length} selected`,
          },
          { label: "Notify", value: notifyEmail || "—" },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}
          >
            <Typography sx={{ fontSize: "13px", color: "#6B6B6B" }}>{label}:</Typography>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#2E7D32" }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* Submit button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          disabled={!isFormValid}
          onClick={() => setConfirmOpen(true)}
          startIcon={<MailOutlineIcon />}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            bgcolor: "#4D44D8",
            px: 3,
            py: 1.25,
            fontSize: "14px",
            fontWeight: 500,
            "&:hover": { bgcolor: "#3a32c0" },
            "&:disabled": { bgcolor: "#C5C5E8" },
          }}
        >
          Request Download & Notify
        </Button>
      </Box>

      {/* ── Confirm Dialog ── */}
      <Dialog
        open={confirmOpen}
        onClose={() => !isSubmitting && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ bgcolor: "#4D44D8", color: "#fff", py: 2.5, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TaskAltIcon sx={{ fontSize: "28px" }} />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
                Confirm Download Request
              </Typography>
              <Typography sx={{ fontSize: "12px", opacity: 0.85, mt: 0.25 }}>
                Review your selection before submitting
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: "24px !important", px: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 3, columnGap: 3, mb: 3 }}>
            {[
              { label: "From", value: fromDisplay },
              { label: "To", value: toDisplay },
              {
                label: "Vehicles",
                value: selectedVehicles.length === 1
                  ? vehicleTableData.find((r) => r.batteryID === selectedVehicles[0])?.licensePlateNo || selectedVehicles[0]
                  : selectedVehicles.length > 1
                  ? `${selectedVehicles.length} selected`
                  : "—",
              },
              { label: "Category", value: selectedType?.label || "—" },
              {
                label: "Output",
                value: selectedType?.outputFormat === "zip" ? "ZIP" : "CSV",
              },
              {
                label: "CAN IDs",
                value: selectedCanIds.length === 0 ? "All" : `${selectedCanIds.length} selected`,
              },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography sx={{ fontSize: "12px", color: "#9B9B9B", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#1A1A1A" }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          {selectedColumns.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "12px", color: "#9B9B9B", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.4px" }}>Extra Columns</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                {selectedColumns.join(", ")}
              </Typography>
            </Box>
          )}

          {notifyEmail && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "12px", color: "#9B9B9B", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.4px" }}>Notify</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>{notifyEmail}</Typography>
            </Box>
          )}

          <Alert
            icon={<WarningAmberIcon sx={{ fontSize: "18px" }} />}
            severity="warning"
            sx={{ borderRadius: "8px", fontSize: "13px", mt: 1 }}
          >
            An email will be sent to <strong>{notifyEmail || "you"}</strong> once ready. Link expires in 7 days.
          </Alert>

          {submitError && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: "8px", fontSize: "13px" }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmOpen(false)}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: "8px", borderColor: "#BBBBBB", color: "#2E2E2E", px: 3, py: 1.25, fontSize: "14px", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <MailOutlineIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              bgcolor: "#4D44D8",
              px: 3,
              py: 1.25,
              fontSize: "14px",
              fontWeight: 500,
              "&:hover": { bgcolor: "#3a32c0" },
              "&:disabled": { bgcolor: "#C5C5E8" },
            }}
          >
            {isSubmitting ? "Submitting..." : "Confirm & Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar notification ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "8px", fontSize: "13px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid2>
  );
};

export default NewDownloadMain;
